import { getApiBaseUrl, getBusinessClient } from '@beatq/sdk-client';
import {
  getAccessToken,
  mapErrorCode,
  moneyAriaLabel,
  readDataFromEnvelope,
  readEntityFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';

type ApiErr = { error?: { code: string; message: string } };

export type Invoice = {
  id?: string;
  number?: string | null;
  reference?: string | null;
  invoiceNumber?: number | null;
  status?: string | null;
  state?: string | null;
  period?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  issuedAt?: string | null;
  issueDate?: string | null;
  dueOn?: string | null;
  creditNoteOfInvoiceId?: string | null;
  billingName?: string | null;
  totalMinor?: number | null;
  subtotalMinor?: number | null;
  vatMinor?: number | null;
  paidAmountMinor?: number | null;
  outstandingMinor?: number | null;
  outstandingAmount?: number | null;
  currency?: string | null;
  dueDate?: string | null;
  lastPaymentReference?: string | null;
  lastPaymentMethod?: string | null;
  lastPaidAt?: string | null;
  purchaseOrderReference?: string | null;
  legalName?: string | null;
  billingEmail?: string | null;
  queries?: InvoiceQuery[] | null;
};

export type InvoiceLine = {
  id?: string;
  description?: string | null;
  amountMinor?: number | null;
  grossAmountMinor?: number | null;
  netAmountMinor?: number | null;
  vatAmountMinor?: number | null;
  quantity?: number | null;
  costCentreCode?: string | null;
  departmentName?: string | null;
  employeeReference?: string | null;
  employeeName?: string | null;
  branchName?: string | null;
  branch?: string | null;
  orderReference?: string | null;
  orderId?: string | null;
  orderedAt?: string | null;
  orderDate?: string | null;
  occurredOn?: string | null;
  vatRate?: string | number | null;
  taxRate?: string | number | null;
  vatRateBps?: number | null;
  lineType?: string | null;
  originalLineId?: string | null;
  creditOfLineId?: string | null;
};

export type InvoiceQuery = {
  id?: string;
  reason?: string | null;
  state?: string | null;
  lineIds?: string[] | null;
  createdAt?: string | null;
  raisedByAccountId?: string | null;
  evidence?: {
    text?: string | null;
    files?: Array<{ filename?: string; contentType?: string; sizeBytes?: number }>;
  } | null;
};

export type BusinessAccount = {
  id?: string;
  legalName?: string | null;
  tradingName?: string | null;
  billingEmail?: string | null;
  contactName?: string | null;
  status?: string | null;
  vatNumber?: string | null;
  paymentTerms?: string | null;
  invoicingFrequency?: string | null;
  billingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  } | null;
  notificationPrefs?: {
    approvalEmail?: boolean;
    budgetEmail?: boolean;
    invoiceEmail?: boolean;
    weeklyDigest?: boolean;
  } | null;
};

export type ApiClient = {
  id?: string;
  clientId?: string | null;
  name?: string | null;
  scopes?: string | null;
  status?: string | null;
  expiresAt?: string | null;
  createdAt?: string | null;
  clientSecret?: string | null;
};

export type ActivityEvent = {
  id?: string;
  action?: string | null;
  category?: string | null;
  summary?: string | null;
  actorId?: string | null;
  createdAt?: string | null;
};

export type SpendReport = {
  reportType?: string | null;
  rptCode?: string | null;
  rowCount?: number | null;
  orderCount?: number | null;
  totalMinor?: number | null;
  averageOrderMinor?: number | null;
  currency?: string | null;
  asAt?: string | null;
};

export type SpendReportRow = {
  groupKey?: string | null;
  groupLabel?: string | null;
  groupBy?: string | null;
  orderCount?: number | null;
  totalMinor?: number | null;
  averageOrderMinor?: number | null;
  amountMinor?: number | null;
  employeeReference?: string | null;
  departmentName?: string | null;
  costCentreCode?: string | null;
  vertical?: string | null;
  orderId?: string | null;
  orderedAt?: string | null;
  sourceStatus?: string | null;
  message?: string | null;
  currency?: string | null;
};

export type ReportExportRun = {
  id?: string;
  runId?: string;
  rptCode?: string | null;
  state?: string | null;
  format?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  expiresAt?: string | null;
  retentionUntil?: string | null;
  rowCount?: number | null;
  downloadUrl?: string | null;
};

export type WebhookClient = {
  id?: string;
  name?: string | null;
  url?: string | null;
  status?: string | null;
};

export type AuthSession = {
  id?: string;
  device?: string | null;
  ipAddress?: string | null;
  lastSeenAt?: string | null;
  status?: string | null;
};

export type AlertItem = {
  id?: string;
  summary?: string | null;
  title?: string | null;
  body?: string | null;
  category?: string | null;
  status?: string | null;
  severity?: string | null;
  subjectKind?: string | null;
  subjectId?: string | null;
  semanticSubject?: string | null;
  semantic_subject?: string | null;
  notificationKey?: string | null;
  amountMinor?: number | null;
  departmentId?: string | null;
  departmentName?: string | null;
  employeeName?: string | null;
  audience?: string | null;
  deepLink?: string | null;
  isRead?: boolean | null;
  createdAt?: string | null;
};

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

function vatRateFromLine(line: InvoiceLine): string | number | null {
  if (line.vatRate != null && line.vatRate !== '') return line.vatRate;
  if (line.taxRate != null && line.taxRate !== '') return line.taxRate;
  if (line.vatRateBps != null && Number.isFinite(line.vatRateBps)) return line.vatRateBps / 100;
  return null;
}

function normalizeInvoice(inv: Invoice): Invoice {
  const number =
    inv.number ?? inv.reference ?? (inv.invoiceNumber != null ? String(inv.invoiceNumber) : null);
  const status = inv.status ?? inv.state ?? null;
  const period =
    inv.period ??
    (inv.periodStart && inv.periodEnd
      ? `${String(inv.periodStart).slice(0, 10)} – ${String(inv.periodEnd).slice(0, 10)}`
      : null);
  const outstanding =
    inv.outstandingMinor ??
    inv.outstandingAmount ??
    (inv.totalMinor != null ? Math.max(0, inv.totalMinor - (inv.paidAmountMinor ?? 0)) : null);
  const dueDate = inv.dueDate ?? inv.dueOn ?? null;
  return {
    ...inv,
    number,
    status,
    period,
    outstandingMinor: outstanding,
    dueDate,
    issuedAt: inv.issuedAt ?? inv.issueDate ?? null,
    legalName: inv.legalName ?? inv.billingName ?? null,
  };
}

function normalizeLine(line: InvoiceLine): InvoiceLine {
  return {
    ...line,
    amountMinor: line.amountMinor ?? line.grossAmountMinor ?? line.netAmountMinor ?? null,
    branchName: line.branchName ?? line.branch ?? null,
    employeeName: line.employeeName ?? line.employeeReference ?? null,
    orderReference: line.orderReference ?? line.orderId ?? null,
    orderedAt: line.orderedAt ?? line.orderDate ?? line.occurredOn ?? null,
    vatRate: vatRateFromLine(line),
  };
}

function normalizeAlert(alert: AlertItem): AlertItem {
  const semantic =
    alert.semanticSubject?.trim() ||
    alert.semantic_subject?.trim() ||
    (alert.subjectKind && alert.subjectId ? `${alert.subjectKind}:${alert.subjectId}` : null);
  return {
    ...alert,
    semanticSubject: semantic,
    isRead: alert.isRead ?? (alert.status ?? '').toUpperCase() === 'READ',
  };
}

export function invoiceFromEnvelope(envelope: unknown): Invoice | undefined {
  const raw =
    readObjectFieldFromEnvelope<Invoice>(envelope, ['invoice']) ??
    readDataFromEnvelope<Invoice>(envelope);
  return raw ? normalizeInvoice(raw) : undefined;
}

export function invoicesFromEnvelope(envelope: unknown): Invoice[] {
  return readListFromEnvelope<Invoice>(envelope, ['invoices', 'items']).map(normalizeInvoice);
}

export function invoiceLinesFromEnvelope(envelope: unknown): InvoiceLine[] {
  return readListFromEnvelope<InvoiceLine>(envelope, ['lines', 'items']).map(normalizeLine);
}

export function invoiceQueriesFromEnvelope(envelope: unknown): InvoiceQuery[] {
  return readListFromEnvelope<InvoiceQuery>(envelope, ['queries', 'items']);
}

export function accountFromEnvelope(envelope: unknown): BusinessAccount | undefined {
  const nested = readObjectFieldFromEnvelope<BusinessAccount>(envelope, ['account']);
  if (nested) return nested;
  const data = readDataFromEnvelope<BusinessAccount & { account?: BusinessAccount }>(envelope);
  if (data && typeof data === 'object' && 'account' in data && data.account) {
    return data.account;
  }
  return data ?? undefined;
}

export function reportFromEnvelope(envelope: unknown): SpendReport | undefined {
  return readEntityFromEnvelope<SpendReport>(envelope, ['report']);
}

export function spendRowsFromEnvelope(envelope: unknown): SpendReportRow[] {
  return readListFromEnvelope<SpendReportRow>(envelope, ['rows', 'items']).filter(
    (r) => !r.sourceStatus,
  );
}

export function exportRunsFromEnvelope(envelope: unknown): ReportExportRun[] {
  return readListFromEnvelope<ReportExportRun>(envelope, ['exports', 'runs', 'items']);
}

export function webhooksFromEnvelope(envelope: unknown): WebhookClient[] {
  return readListFromEnvelope<WebhookClient>(envelope, ['webhooks', 'subscriptions', 'items']);
}

export function sessionsFromEnvelope(envelope: unknown): AuthSession[] {
  return readListFromEnvelope<AuthSession>(envelope, ['sessions', 'items']);
}

export function alertsFromEnvelope(envelope: unknown): AlertItem[] {
  return readListFromEnvelope<AlertItem>(envelope, ['alerts', 'items', 'notifications']).map(
    normalizeAlert,
  );
}

export function apiClientsFromEnvelope(envelope: unknown): ApiClient[] {
  return readListFromEnvelope<ApiClient>(envelope, ['clients', 'apiClients', 'items']);
}

export function activityFromEnvelope(envelope: unknown): ActivityEvent[] {
  return readListFromEnvelope<ActivityEvent>(envelope, ['activity', 'events', 'items']);
}

export async function listInvoices() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/invoices');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getInvoice(invoiceId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/invoices/{id}', {
    params: { path: { id: invoiceId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getInvoiceLines(invoiceId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/invoices/{id}/lines', {
    params: { path: { id: invoiceId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function loadInvoiceDetail(invoiceId: string) {
  const invoiceEnv = await getInvoice(invoiceId);
  const invoice = invoiceFromEnvelope(invoiceEnv);

  let lines: InvoiceLine[] = [];
  let linesError: string | null = null;
  try {
    const linesEnv = await getInvoiceLines(invoiceId);
    lines = invoiceLinesFromEnvelope(linesEnv);
  } catch (err) {
    linesError = err instanceof Error ? err.message : 'Could not load invoice lines.';
  }

  let queries: InvoiceQuery[] = Array.isArray(invoice?.queries) ? invoice!.queries! : [];
  try {
    const queriesEnv = await listInvoiceQueries(invoiceId);
    const fromList = invoiceQueriesFromEnvelope(queriesEnv);
    if (fromList.length) queries = fromList;
  } catch {
    /* GET queries is optional when the invoice already embeds them */
  }

  return { invoice, lines, queries, linesError };
}

export async function listInvoiceQueries(invoiceId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/invoices/{id}/queries', {
    params: { path: { id: invoiceId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function submitInvoiceQuery(
  invoiceId: string,
  body: {
    reason: string;
    lineIds?: string[];
    evidence?: string;
    evidenceFiles?: Array<{
      filename: string;
      contentType?: string;
      contentBase64?: string;
      reference?: string;
    }>;
  },
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/invoices/{id}/queries', {
    params: { path: { id: invoiceId } },
    body: {
      reason: body.reason,
      lineIds: body.lineIds ?? [],
      evidence: body.evidence?.trim() || undefined,
      evidenceFiles: body.evidenceFiles?.length ? body.evidenceFiles : undefined,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function recordInvoicePayment(
  invoiceId: string,
  body: {
    amountMinor: number;
    reference?: string;
    method?: string;
    paidAt?: string;
  },
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/invoices/{id}/payments', {
    params: { path: { id: invoiceId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function exportInvoice(invoiceId: string, body: { format?: string }) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/invoices/{id}/exports', {
    params: { path: { id: invoiceId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export function exportDownloadUrlFromEnvelope(envelope: unknown): string | null {
  const root = envelope as { data?: { url?: string }; url?: string };
  return root?.data?.url ?? root?.url ?? null;
}

export function formatInvoiceMoney(minor: number | null | undefined, currency = 'GBP'): string {
  if (minor == null || !Number.isFinite(minor)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

/** Wireframe 32 / 52 — amounts announced as words, never as a spinner value. */
export function invoiceMoneyAria(minor: number | null | undefined, currency = 'GBP'): string {
  if (minor == null || !Number.isFinite(minor)) return 'Amount not stated';
  return moneyAriaLabel(minor, currency);
}

/** Match SDK client: empty base = same-origin / Vite proxy (not hardcoded :3000). */
function apiBaseUrl(): string {
  return getApiBaseUrl().replace(/\/$/, '');
}

/** Ensure export artifact exists, then download via authenticated GET. */
export async function downloadInvoiceExport(invoiceId: string, format: 'CSV' | 'PDF') {
  const envelope = await exportInvoice(invoiceId, { format });
  const relative = exportDownloadUrlFromEnvelope(envelope);
  const path =
    relative ?? `/business/v1/invoices/${encodeURIComponent(invoiceId)}/downloads?format=${format}`;
  const url = path.startsWith('http') ? path : `${apiBaseUrl()}${path}`;
  const token = getAccessToken();
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `invoice-${invoiceId}.${format === 'PDF' ? 'pdf' : 'csv'}`;
  a.click();
  URL.revokeObjectURL(objectUrl);
  return envelope;
}

export async function getSpendReport(
  reportType = 'corporate-spend',
  query: {
    from?: string;
    to?: string;
    groupBy?: string;
    departmentId?: string;
    costCentreId?: string;
    employeeId?: string;
    vertical?: string;
  } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/reports/{reportType}', {
    params: {
      path: { reportType },
      query: Object.fromEntries(
        Object.entries({
          from: query.from,
          to: query.to,
          groupBy: query.groupBy,
          departmentId: query.departmentId,
          costCentreId: query.costCentreId,
          employeeId: query.employeeId,
          vertical: query.vertical,
        }).filter(([, value]) => Boolean(value)),
      ) as never,
    },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listReportExports() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/reports/exports');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function queueReportExport(body: {
  reportType?: string;
  format?: string;
  parameters?: Record<string, unknown>;
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/reports/exports', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getReportExportOutput(runId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/reports/runs/{runId}/output', {
    params: { path: { runId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getAccount() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/account');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateAccount(body: Record<string, unknown>) {
  const client = getBusinessClient();
  const { data, error, response } = await client.PATCH('/business/v1/account', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listWebhooks() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/webhooks');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function createWebhook(body: Record<string, unknown>) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/webhooks', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listApiClients() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/api-clients');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function createApiClient(body: {
  name: string;
  scopes?: string;
  expiresAt?: string;
  expiresInDays?: number;
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/api-clients', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function revokeApiClient(clientId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.DELETE('/business/v1/api-clients/{id}', {
    params: { path: { id: clientId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listAccountActivity(
  query: { category?: string; from?: string; to?: string } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/activity', {
    params: {
      query: Object.fromEntries(
        Object.entries({
          category: query.category,
          from: query.from,
          to: query.to,
        }).filter(([, value]) => Boolean(value)),
      ) as never,
    },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function downloadActivityExport(
  query: { category?: string; from?: string; to?: string } = {},
) {
  const params = new URLSearchParams();
  if (query.category) params.set('category', query.category);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const qs = params.toString();
  const path = `/business/v1/activity/export${qs ? `?${qs}` : ''}`;
  const url = `${apiBaseUrl()}${path}`;
  const token = getAccessToken();
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Activity export failed (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'account-activity.csv';
  a.click();
  URL.revokeObjectURL(objectUrl);
}

export async function listAuthSessions() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/auth/sessions');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function revokeAuthSession(sessionId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.DELETE('/business/v1/auth/sessions/{id}', {
    params: { path: { id: sessionId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function loadAlertsPanel(opts?: {
  unreadOnly?: boolean;
  category?: string;
  departmentId?: string;
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/alerts', {
    params: {
      query: Object.fromEntries(
        Object.entries({
          unreadOnly: opts?.unreadOnly ? 'true' : undefined,
          category: opts?.category || undefined,
          departmentId: opts?.departmentId || undefined,
        }).filter(([, value]) => Boolean(value)),
      ) as never,
    },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function markAlertRead(alertId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/alerts/{id}/read', {
    params: { path: { id: alertId } },
    body: {} as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function createRealtimeConnectionTicket(
  body: {
    purpose?: string;
    businessAccountId?: string;
  } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/realtime/connection-tickets', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
